import { Box, Slider, styled, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { OverlayLabel } from './OverlayLabel';
import {
    selectedPETColorMappingState,
    PETColorMap,
} from '~/state/atoms/image/petImageAtom';
import { secondaryImageState } from '~/state/atoms/image/imageAtoms';
import { FormattedMessage } from 'react-intl';
import { useWindowLevelSubscription } from '~/hooks/useWindowLevelSubscription';
import { domainEventBus, WindowLevelSetEvent } from '~/domain/events';
import { WindowLevel } from '~/domain/patient/windowLevel';
import usePetImage from '~/hooks/image/usePetImage';

const PetColorwashSlider = styled(Slider)(({ theme }) => ({
    height: 130,
    width: 12,
    boxSizing: 'border-box',
    pointerEvents: 'auto',
    '& .MuiSlider-thumb': {
        border: '1px solid #fff',
        backgroundColor: `${theme.palette.secondary.main}`,
        width: 16,
        height: 6,
        borderRadius: 0,
    },
    '& .MuiSlider-valueLabel': {
        backgroundColor: 'transparent',
        padding: 0,
        right: '40px',
        left: 'auto',
        top: '10px',
    },
    '& .MuiSlider-mark': {
        background: 'transparent',
    },
    '& .MuiSlider-rail': {
        height: '100%',
        background: 'var(--slider-gradient)',
        opacity: 0.9,
        borderRadius: 0,
    },
    '& .MuiSlider-track': {
        background: 'transparent',
        border: `2px solid ${theme.palette.background.paper}`,
        borderLeft: 'none',
        borderRight: 'none',
        borderRadius: 0,
    },
}));

const ValueLabelBox = styled(Box)(({ theme }) => ({
    ...theme.mixins.glassOverlay,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
}));

const CONVERGENCE_TOLERANCE = 0.015;
const GRADIENT_SAMPLES = 20;

const sampleColorMapAt = (colorMap: PETColorMap, t: number): string => {
    const clampedT = Math.max(0, Math.min(1, t));
    const stops = colorMap.stops;

    let lowerIdx = 0;
    for (let j = 0; j < stops.length - 1; j++) {
        if (
            clampedT >= stops[j].position &&
            clampedT <= stops[j + 1].position
        ) {
            lowerIdx = j;
            break;
        }
    }

    const lower = stops[lowerIdx];
    const upper = stops[Math.min(lowerIdx + 1, stops.length - 1)];

    const range = upper.position - lower.position;
    const localT = range > 0 ? (clampedT - lower.position) / range : 0;

    const r = Math.round(
        (lower.color[0] + (upper.color[0] - lower.color[0]) * localT) * 255
    );
    const g = Math.round(
        (lower.color[1] + (upper.color[1] - lower.color[1]) * localT) * 255
    );
    const b = Math.round(
        (lower.color[2] + (upper.color[2] - lower.color[2]) * localT) * 255
    );

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const buildDynamicGradient = (
    colorMap: PETColorMap,
    lower: number,
    upper: number
): string => {
    const range = upper - lower;
    if (range <= 0.001) {
        return 'linear-gradient(to bottom, transparent, transparent)';
    }
    const toCss = (v: number) => `${((1 - v) * 100).toFixed(2)}%`;
    const stops: string[] = [];
    const topColor = sampleColorMapAt(colorMap, 1.0);
    stops.push(`${topColor} 0%`);
    stops.push(`${topColor} ${toCss(upper)}`);

    for (let i = 1; i <= GRADIENT_SAMPLES; i++) {
        const fraction = i / GRADIENT_SAMPLES;
        const colormapT = 1 - fraction;
        const sliderPos = upper - fraction * range;
        const color = sampleColorMapAt(colorMap, colormapT);
        stops.push(`${color} ${toCss(sliderPos)}`);
    }

    stops.push(`transparent ${toCss(lower - 0.001)}`);
    stops.push(`transparent 100%`);

    return `linear-gradient(to bottom, ${stops.join(', ')})`;
};

export const PetFusionColorwashSlider = () => {
    const secondaryImage = useRecoilValue(secondaryImageState);
    const selectedColorMap = useRecoilValue(selectedPETColorMappingState);
    const windowLevel = useWindowLevelSubscription(secondaryImage ?? undefined);
    const {
        maxPixelValue,
        maxIntensityLabel,
        formatNormalizedPetValue,
        navigateToMaxIntensity,
    } = usePetImage();
    const [dragValue, setDragValue] = useState<[number, number] | null>(null);
    const dragRef = useRef<[number, number] | null>(null);

    useEffect(() => {
        dragRef.current = null;
        setDragValue(null);

        if (secondaryImage && maxPixelValue > 0) {
            const ww = Math.ceil(maxPixelValue);
            const wc = Math.round(maxPixelValue * 0.5);
            domainEventBus.publishSync(
                new WindowLevelSetEvent(
                    new WindowLevel(ww, wc, 0, Math.ceil(maxPixelValue)),
                    secondaryImage.seriesUid,
                    'user'
                )
            );
        }
    }, [secondaryImage, maxPixelValue]);

    const derivedValue = useMemo<[number, number]>(() => {
        if (!windowLevel || maxPixelValue <= 0) {
            return [0, 1];
        }
        const minPx = windowLevel.level - windowLevel.window * 0.5;
        const maxPx = windowLevel.level + windowLevel.window * 0.5;
        return [
            Math.max(0, Math.min(1, minPx / maxPixelValue)),
            Math.max(0, Math.min(1, maxPx / maxPixelValue)),
        ];
    }, [windowLevel, maxPixelValue]);

    useEffect(() => {
        const drag = dragRef.current;
        if (!drag) {
            return;
        }
        const [dl, du] = drag;
        const [vl, vu] = derivedValue;
        if (
            Math.abs(dl - vl) < CONVERGENCE_TOLERANCE &&
            Math.abs(du - vu) < CONVERGENCE_TOLERANCE
        ) {
            dragRef.current = null;
            setDragValue(null);
        }
    }, [derivedValue]);

    const patientWeight = useMemo(() => {
        if (secondaryImage?.patientWeightKg) {
            return (
                <>
                    <FormattedMessage id="weight" />:{' '}
                    {secondaryImage.patientWeightKg.toFixed(1)} kg
                </>
            );
        }
        return null;
    }, [secondaryImage?.patientWeightKg]);

    const sliderValue = dragValue ?? derivedValue;

    const gradientColors = useMemo(
        () =>
            buildDynamicGradient(
                selectedColorMap,
                sliderValue[0],
                sliderValue[1]
            ),
        [selectedColorMap, sliderValue]
    );

    const onColorwashValueChange = (
        _e: Event,
        value: number[] | number
    ): void => {
        if (!secondaryImage) {
            return;
        }
        const newValue = value as [number, number];
        dragRef.current = newValue;
        setDragValue(newValue);
        const [lower, upper] = newValue;
        if (upper <= lower) {
            return;
        }

        const minPx = lower * maxPixelValue;
        const maxPx = upper * maxPixelValue;
        const ww = Math.max(1, Math.round(maxPx - minPx));
        const wc = Math.round((minPx + maxPx) * 0.5);

        domainEventBus.publishSync(
            new WindowLevelSetEvent(
                new WindowLevel(ww, wc, 0, Math.ceil(maxPixelValue)),
                secondaryImage.seriesUid,
                'user'
            )
        );
    };

    if (!secondaryImage || !windowLevel) {
        return null;
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            gap={0.25}
        >
            <OverlayLabel
                onClick={navigateToMaxIntensity}
                data-testid="pet-max-intensity-label"
                sx={{
                    mb: 1.5,
                    mr: -1.5,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                }}
            >
                <FormattedMessage id="max" />: {maxIntensityLabel}
            </OverlayLabel>
            {patientWeight && (
                <OverlayLabel sx={{ mt: -1.5, mb: 1, mr: -1.5 }} fontSize={10}>
                    {patientWeight}
                </OverlayLabel>
            )}
            <PetColorwashSlider
                orientation="vertical"
                value={sliderValue}
                step={0.01}
                min={0}
                max={1}
                size="small"
                onChange={onColorwashValueChange}
                valueLabelDisplay="on"
                data-testid="pet-colorwash-slider"
                style={
                    {
                        '--slider-gradient': gradientColors,
                    } as React.CSSProperties
                }
                valueLabelFormat={(value) => (
                    <ValueLabelBox>
                        <OverlayLabel fontSize={11}>
                            {(value * 100).toFixed(0)}%
                        </OverlayLabel>
                        <Typography fontSize={9} lineHeight={1}>
                            {formatNormalizedPetValue(value)}
                        </Typography>
                    </ValueLabelBox>
                )}
            />
        </Box>
    );
};

export default PetFusionColorwashSlider;
